import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import '../styles/questionbank.css';
import '../styles/newrecruiter.css';
import '../styles/superadmin.css';
import logo from '../assets/logo.png';
import ProfileDropdown from '../components/ProfileDropdown';
import PrettySelect from '../components/PrettySelect';
import Toast from '../components/Toast';
import { clearDraft, loadDraft, saveDraft } from '../utils/draftStorage';
import { getActiveToken, getStoredUserForRole } from '../utils/authStorage';

const EMPTY_QUESTION_FORM = {
  section_id: '',
  difficulty: 'Medium',
  marks: '01',
  negative_marks: '00',
  question_text: '',
  options: ['', '', '', ''],
  correct_option: 0,
};

const getQuestionDraftKey = (id) => id ? `question-bank:edit:${id}` : 'question-bank:create';
const BULK_QUESTION_DRAFT_KEY = 'question-bank:bulk-section';

const preventValueScroll = (event) => {
  event.preventDefault();
};

const keepDigitsOnly = (value) => value.replace(/[^\d]/g, '');

const QuestionPreviewCell = ({ text }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const node = textRef.current;
    if (!node) return undefined;

    const updateTruncation = () => {
      setIsTruncated(node.scrollWidth > node.clientWidth + 1);
    };

    updateTruncation();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateTruncation);
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateTruncation);
    return () => window.removeEventListener('resize', updateTruncation);
  }, [text]);

  return (
    <div className={`qb-col-question qb-row-text qb-question-preview ${isTruncated ? 'qb-question-preview--truncated' : ''}`}>
      <span ref={textRef} className="qb-question-preview-text">{text}</span>
      {isTruncated ? (
        <div className="qb-question-preview-tooltip">
          {text}
        </div>
      ) : null}
    </div>
  );
};

/* ── Sidebar Icons ── */
const IconHome = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_home)">
      <path d="M9.99638 13.9949V8.6635C9.99638 8.48675 9.92617 8.31724 9.80119 8.19226C9.67621 8.06728 9.5067 7.99707 9.32995 7.99707H6.66423C6.48748 7.99707 6.31798 8.06728 6.193 8.19226C6.06802 8.31724 5.9978 8.48675 5.9978 8.6635V13.9949" stroke={active ? 'white' : '#26282B'} strokeWidth="1.33286" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M1.99927 6.6641C1.99922 6.47021 2.04148 6.27865 2.12308 6.10278C2.20469 5.9269 2.32368 5.77095 2.47177 5.6458L7.13678 1.64788C7.37735 1.44456 7.68215 1.33301 7.99714 1.33301C8.31212 1.33301 8.61693 1.44456 8.8575 1.64788L13.5225 5.6458C13.6706 5.77095 13.7896 5.9269 13.8712 6.10278C13.9528 6.27865 13.9951 6.47021 13.995 6.6641V12.662C13.995 13.0155 13.8546 13.3545 13.6046 13.6044C13.3547 13.8544 13.0156 13.9948 12.6621 13.9948H3.33213C2.97863 13.9948 2.63961 13.8544 2.38965 13.6044C2.13969 13.3545 1.99927 13.0155 1.99927 12.662V6.6641Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.33286" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_home"><rect width="15.9943" height="15.9943" fill="white"/></clipPath>
    </defs>
  </svg>
);

const IconCandidates = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" 
      stroke={active ? 'white' : '#26282B'} 
      strokeWidth="1.66667" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <circle 
      cx="9" 
      cy="7" 
      r="4" 
      stroke={active ? 'white' : '#26282B'} 
      strokeWidth="1.66667" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M23 21v-2a4 4 0 0 0-3-3.87" 
      stroke={active ? 'white' : '#26282B'} 
      strokeWidth="1.66667" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M16 3.13a4 4 0 0 1 0 7.75" 
      stroke={active ? 'white' : '#26282B'} 
      strokeWidth="1.66667" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const IconSections = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 5H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round"/>
    <path d="M2.5 10H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round"/>
    <path d="M2.5 15H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round"/>
  </svg>
);

const IconQuestionBank = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 14.167H10.0083" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.4999 1.66699H4.99992C4.55789 1.66699 4.13397 1.84259 3.82141 2.15515C3.50885 2.46771 3.33325 2.89163 3.33325 3.33366V16.667C3.33325 17.109 3.50885 17.5329 3.82141 17.8455C4.13397 18.1581 4.55789 18.3337 4.99992 18.3337H14.9999C15.4419 18.3337 15.8659 18.1581 16.1784 17.8455C16.491 17.5329 16.6666 17.109 16.6666 16.667V5.83366L12.4999 1.66699Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.58325 7.49982C7.78323 6.94867 8.17056 6.48535 8.67753 6.19084C9.18449 5.89633 9.77884 5.78937 10.3567 5.88865C10.9345 5.98793 11.4591 6.28715 11.8387 6.73399C12.2182 7.18083 12.4287 7.74686 12.4333 8.33315C12.4333 9.99982 9.93325 10.8331 9.93325 10.8331" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconExamManagement = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.5 1.66699H7.49996C7.03972 1.66699 6.66663 2.04009 6.66663 2.50033V4.16699C6.66663 4.62723 7.03972 5.00033 7.49996 5.00033H12.5C12.9602 5.00033 13.3333 4.62723 13.3333 4.16699V2.50033C13.3333 2.04009 12.9602 1.66699 12.5 1.66699Z" stroke="#26282B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M13.3334 3.33301H15C15.4421 3.33301 15.866 3.5086 16.1786 3.82116C16.4911 4.13372 16.6667 4.55765 16.6667 4.99967V16.6663C16.6667 17.1084 16.4911 17.5323 16.1786 17.8449C15.866 18.1574 15.4421 18.333 15 18.333H5.00004C4.55801 18.333 4.13409 18.1574 3.82153 17.8449C3.50897 17.5323 3.33337 17.1084 3.33337 16.6663V4.99967C3.33337 4.55765 3.50897 4.13372 3.82153 3.82116C4.13409 3.5086 4.55801 3.33301 5.00004 3.33301H6.66671" stroke="#26282B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 9.16699H13.3333" stroke="#26282B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 13.333H13.3333" stroke="#26282B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M6.66663 9.16699H6.67496" stroke="#26282B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M6.66663 13.333H6.67496" stroke="#26282B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>

);

const IconAnalytics = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 2.5V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 14.1667V7.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.8333 14.167V4.16699" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.66675 14.167V11.667" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconDrive = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.333 6.667h13.334M5 3.333h10c.92 0 1.667.746 1.667 1.667v10c0 .92-.746 1.667-1.667 1.667H5A1.667 1.667 0 0 1 3.333 15V5c0-.92.746-1.667 1.667-1.667Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 10h.01M10 10h3M7 13h.01M10 13h3" stroke={active ? 'white' : '#26282B'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

const sidebarItems = [
  { label: 'Home',             Icon: IconHome,           path: '/recruiter' },
  { label: 'Drives',           Icon: IconDrive,          path: '/drives' },
  { label: 'Exam Management',  Icon: IconExamManagement, path: '/exam-management' },
  { label: 'Candidates',       Icon: IconCandidates,     path: '/candidates' },
  { label: 'Sections',         Icon: IconSections,       path: '/sections' },
  { label: 'Question Bank',    Icon: IconQuestionBank,   path: '/question-bank' },
  { label: 'Analytics',        Icon: IconAnalytics,      path: '/analytics' },
];

const adminSidebarItems = [
  { label: 'Dashboard',     Icon: IconHome,           path: '/superadmin' },
  { label: 'Recruiters',    Icon: IconCandidates,     path: '/superadmin?tab=Recruiters' },
  { label: 'Sections',      Icon: IconSections,       path: '/superadmin/sections' },
  { label: 'Question Bank', Icon: IconQuestionBank,   path: '/superadmin/questionbank' },
  { label: 'Analytics',     Icon: IconAnalytics,      path: '/superadmin?tab=Analytics' },
  { label: 'Logs',          Icon: IconExamManagement, path: '/superadmin?tab=Logs' },
  { label: 'SMTP Settings', Icon: IconExamManagement, path: '/superadmin?tab=SMTP Settings' },
];

/* ── Difficulty Badge ── */
const DifficultyBadge = ({ level }) => {
const config = {
  Easy: {
    color: '#0BA315', bg: '#F5FBF6', border: 'none', padding: '5.02px 8.37px', gap: '8.37px', borderRadius: '4.19px', fontSize:'12.885px', lineHeight: '17px',
    icon: (
      <svg width="31" height="21" viewBox="0 0 31 21" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path opacity="0.1" d="M26.9094 6.15221C27.0036 5.73782 26.8623 5.32343 26.5799 5.04716C24.32 2.42268 21.1657 0.626978 17.7289 0.028411C17.3051 -0.0636762 16.9285 0.0744545 16.5989 0.350716C16.2694 0.626978 16.0811 0.995327 16.0811 1.40972V5.41551C16.0811 6.06012 16.5048 6.5666 17.1168 6.75078C18.5763 7.11913 19.8945 7.85582 20.9303 8.91483C21.2128 9.19109 21.5423 9.32922 21.919 9.32922C22.1544 9.32922 22.3898 9.28318 22.6252 9.14505L26.1562 7.11913C26.5799 6.93495 26.8623 6.5666 26.9094 6.15221Z" fill="#0BA315"/>
        <path d="M8.56822 13.8577C8.53362 14.0501 8.60314 14.2274 8.78833 14.3254C9.996 15.1282 14.028 17.7061 14.8804 17.8527C16.4542 18.1235 17.9964 17.0665 18.2732 15.5273C18.55 13.9882 17.4693 12.4799 15.8955 12.2092C15.043 12.0625 10.3536 13.1401 8.93828 13.4917C8.73003 13.5219 8.60283 13.6654 8.56822 13.8577ZM15.4763 14.3519C15.8041 14.4083 16.0473 14.7477 15.9896 15.0684C15.932 15.389 15.585 15.6268 15.2571 15.5704C14.9292 15.514 14.6861 15.1747 14.7437 14.854C14.8014 14.5334 15.1098 14.322 15.4763 14.3519Z" fill="#0BA315"/>
        <path opacity="0.1" d="M14.106 5.41403V1.40824C14.106 0.993847 13.9176 0.625498 13.5881 0.349236C13.2585 0.0729747 12.8348 -0.0191124 12.4582 0.0269312C9.02133 0.625498 5.86698 2.4212 3.60714 5.04568C3.32467 5.36799 3.18343 5.78238 3.27759 6.15073C3.37175 6.56512 3.60714 6.88743 3.98378 7.11765L7.51477 9.14357C7.75017 9.2817 7.98557 9.32774 8.22097 9.32774C8.59761 9.32774 8.92717 9.18961 9.20965 8.91335C10.2454 7.85434 11.6107 7.11765 13.0231 6.7493C13.6822 6.61117 14.106 6.05864 14.106 5.41403Z" fill="#0BA315"/>
        <path opacity="0.1" d="M29.2621 9.51164C29.1209 9.14329 28.8384 8.82099 28.4147 8.68285C28.038 8.54472 27.5672 8.59077 27.2377 8.77494L23.6596 10.8469C23.0946 11.1692 22.8122 11.8138 23.0005 12.4124C23.4242 13.7937 23.4242 15.3131 23.0005 16.7405C22.8122 17.3391 23.0946 17.9837 23.6596 18.306L27.1906 20.3319C27.5672 20.5161 27.991 20.5621 28.3676 20.424C28.7442 20.2858 29.0738 19.9635 29.215 19.5952C30.4391 16.3261 30.4391 12.7807 29.2621 9.51164Z" fill="#0BA315"/>
        <path d="M6.52056 10.8507L2.94249 8.82475C2.56585 8.64057 2.14213 8.59453 1.7655 8.73266C1.38886 8.87079 1.0593 9.1931 0.918058 9.56144C-0.306019 12.7845 -0.306019 16.3759 0.918058 19.599C1.0593 19.9673 1.38886 20.2896 1.7655 20.4277C2.14213 20.5659 2.61293 20.5198 2.94249 20.3356L6.52056 18.3558C7.08552 18.0335 7.368 17.3889 7.17968 16.7903C6.75596 15.3629 6.75596 13.8895 7.17968 12.4622C7.32092 11.7715 7.03844 11.1269 6.52056 10.8507Z" fill="#0BA315"/>
      </svg>
    )
  },
  Medium: {
    color: '#F59E0B', bg: '#FFFBF5', border: 'none', padding: '5.65px 9.42px',gap: '9.42px',borderRadius: '4.71px',fontSize: '14.4957px',lineHeight: '19px',
    icon: (
      <svg width="24" height="17" viewBox="0 0 24 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path opacity="0.1" d="M21.1939 4.95534C21.268 4.62156 21.1568 4.28779 20.9343 4.06527C19.1541 1.95136 16.6694 0.505003 13.9621 0.0228838C13.6283 -0.0512884 13.3316 0.0599699 13.072 0.282487C12.8124 0.505003 12.6641 0.801692 12.6641 1.13547V4.36196C12.6641 4.88117 12.9978 5.28911 13.48 5.43746C14.6296 5.73415 15.668 6.32752 16.4839 7.1805C16.7065 7.40302 16.9661 7.51428 17.2627 7.51428C17.4482 7.51428 17.6336 7.47719 17.819 7.36594L20.6005 5.73415C20.9343 5.5858 21.1568 5.28911 21.1939 4.95534Z" fill="#F59E0B"/>
        <path opacity="0.3" d="M21.1939 4.95534C21.268 4.62156 21.1568 4.28779 20.9343 4.06527C19.1541 1.95136 16.6694 0.505003 13.9621 0.0228838C13.6283 -0.0512884 13.3316 0.0599699 13.072 0.282487C12.8124 0.505003 12.6641 0.801692 12.6641 1.13547V4.36196C12.6641 4.88117 12.9978 5.28911 13.48 5.43746C14.6296 5.73415 15.668 6.32752 16.4839 7.1805C16.7065 7.40302 16.9661 7.51428 17.2627 7.51428C17.4482 7.51428 17.6336 7.47719 17.819 7.36594L20.6005 5.73415C20.9343 5.5858 21.1568 5.28911 21.1939 4.95534Z" fill="#F59E0B"/>
        <path d="M11.9693 7.47853C11.8119 7.47853 11.6808 7.5572 11.6284 7.71454C11.1563 8.7635 9.66157 12.2513 9.66157 12.9331C9.66157 14.1918 10.7105 15.2408 11.9693 15.2408C13.228 15.2408 14.277 14.1918 14.277 12.9331C14.277 12.2513 12.7822 8.7635 12.3102 7.71454C12.2577 7.5572 12.1266 7.47853 11.9693 7.47853ZM12.52 12.9069C12.52 13.1691 12.284 13.4051 12.0217 13.4051C11.7595 13.4051 11.5235 13.1691 11.5235 12.9069C11.5235 12.6446 11.7595 12.4086 12.0217 12.4086C12.284 12.4086 12.4937 12.6184 12.52 12.9069Z" fill="#F59E0B"/>
        <path d="M11.1108 4.36185V1.13536C11.1108 0.801587 10.9625 0.504898 10.7028 0.282381C10.4432 0.0598645 10.1095 -0.0143076 9.81278 0.0227785C7.1055 0.504898 4.62073 1.95126 2.84059 4.06517C2.61808 4.32477 2.50682 4.65854 2.53839 4.87562C2.61138 5.20408 2.79386 5.45955 3.08583 5.64203L5.82302 7.24786C6.0055 7.35734 6.18798 7.39384 6.37046 7.39384C6.66243 7.39384 6.9179 7.28435 7.13688 7.06538C7.93979 6.22597 8.99817 5.64203 10.0931 5.35007C10.604 5.24058 10.9325 4.80263 10.9325 4.29168Z" fill="#F59E0B"/>
        <path opacity="0.1" d="M23.05 7.66291C22.9387 7.36622 22.7162 7.10661 22.3824 6.99535C22.0858 6.8841 21.7149 6.92118 21.4553 7.06953L18.6368 8.7384C18.1917 8.99801 17.9692 9.51721 18.1175 9.99933C18.4513 11.1119 18.4513 12.3358 18.1175 13.4854C17.9692 13.9675 18.1917 14.4868 18.6368 14.7464L21.4182 16.3781C21.7149 16.5265 22.0487 16.5636 22.3454 16.4523C22.6421 16.3411 22.9017 16.0815 23.0129 15.7848C23.9772 13.1516 23.9772 10.296 23.05 7.66291Z" fill="#F59E0B"/>
        <path d="M5.13643 8.73745L2.31788 7.10566C2.02119 6.95732 1.68742 6.92023 1.39073 7.03149C1.09404 7.14275 0.834438 7.40235 0.723179 7.69904C-0.24106 10.2951 -0.24106 13.1878 0.723179 15.7838C0.834438 16.0805 1.09404 16.3401 1.39073 16.4514C1.68742 16.5626 2.05828 16.5255 2.31788 16.3772L5.13643 14.7825C5.58146 14.5229 5.80398 14.0037 5.65563 13.5216C5.32186 12.3719 5.32186 11.1851 5.65563 10.0355C5.76689 9.47917 5.54437 8.95997 5.13643 8.73745Z" fill="#F59E0B"/>
      </svg>
    )
  },
  Hard: {
    color: '#F50B0B', bg: '#FFF5F5', border: 'none', padding: '5.56px 9.27px', gap: '9.27px', borderRadius: '4.63px', fontSize: '14.2524px', lineHeight: '18px',
    icon: (
      <svg width="24" height="17" viewBox="0 0 24 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.8584 4.87648C20.9314 4.54802 20.8219 4.21955 20.603 4.00058C18.8511 1.92031 16.4059 0.496967 13.7417 0.0225196C13.4133 -0.0504723 13.1213 0.0590155 12.8658 0.277991C12.6103 0.496967 12.4644 0.788935 12.4644 1.1174V4.29255C12.4644 4.80349 12.7928 5.20495 13.2673 5.35093C14.3986 5.6429 15.4205 6.22683 16.2234 7.06624C16.4424 7.28521 16.6979 7.3947 16.9899 7.3947C17.1723 7.3947 17.3548 7.35821 17.5373 7.24872L20.2745 5.6429C20.603 5.49691 20.8219 5.20495 20.8584 4.87648Z" fill="#F50B0B"/>
        <path d="M16.9039 11.5606C16.9039 11.4058 16.8265 11.2767 16.6716 11.2251C15.6394 10.7606 12.2071 9.28962 11.5361 9.28962C10.2974 9.28962 9.26517 10.3219 9.26517 11.5606C9.26517 12.7993 10.2974 13.8316 11.5361 13.8316C12.2071 13.8316 15.6394 12.3606 16.6716 11.8961C16.8265 11.8445 16.9039 11.5606 16.9039 11.5606ZM11.562 12.1025C11.3039 12.1025 11.0716 11.8703 11.0716 11.6122C11.0716 11.3541 11.3039 11.1219 11.562 11.1219C11.82 11.1219 12.0523 11.3541 12.0523 11.6122C12.0523 11.8703 11.8458 12.0767 11.562 12.1025Z" fill="#F50B0B"/>
        <path d="M10.9325 4.29168V1.11654C10.9325 0.788072 10.7865 0.496104 10.531 0.277129C10.2755 0.0581529 9.94707 -0.0148389 9.6551 0.021657C6.9909 0.496104 4.54567 1.91945 2.79386 3.99972C2.57488 4.25519 2.4654 4.58365 2.53839 4.87562C2.61138 5.20408 2.79386 5.45955 3.08583 5.64203L5.82302 7.24786C6.0055 7.35734 6.18798 7.39384 6.37046 7.39384C6.66243 7.39384 6.9179 7.28435 7.13688 7.06538C7.93979 6.22597 8.99817 5.64203 10.0931 5.35007C10.604 5.24058 10.9325 4.80263 10.9325 4.29168Z" fill="#F50B0B"/>
        <path d="M22.6829 7.53699C22.5734 7.24503 22.3545 6.98956 22.026 6.88007C21.734 6.77058 21.3691 6.80708 21.1136 6.95306L18.3399 8.59538C17.902 8.85085 17.683 9.36179 17.829 9.83624C18.1574 10.9311 18.1574 12.1355 17.829 13.2669C17.683 13.7413 17.902 14.2523 18.3399 14.5077L21.0771 16.1135C21.3691 16.2595 21.6975 16.296 21.9895 16.1865C22.2815 16.077 22.537 15.8216 22.6464 15.5296C23.5953 12.9384 23.5953 10.1282 22.6829 7.53699Z" fill="#F50B0B"/>
        <path d="M5.05469 8.59794L2.281 6.99212C1.98903 6.84614 1.66057 6.80964 1.3686 6.91913C1.07663 7.02862 0.821159 7.28409 0.711671 7.57606C-0.237224 10.1308 -0.237224 12.9775 0.711671 15.5322C0.821159 15.8241 1.07663 16.0796 1.3686 16.1891C1.66057 16.2986 2.02553 16.2621 2.281 16.1161L5.05469 14.5468C5.49264 14.2913 5.71162 13.7804 5.56563 13.3059C5.23717 12.1745 5.23717 11.0067 5.56563 9.8753C5.67512 9.32786 5.45614 8.81692 5.05469 8.59794Z" fill="#F50B0B"/>
      </svg>
    )
  },
};

    const c = config[level] || config.Easy;
    return (
    <span
        className="qb-difficulty-badge"
        style={{
        color: c.color,
        background: c.bg,
        border: c.border,
        padding: c.padding,
        gap: c.gap,
        borderRadius: c.borderRadius,
        fontSize: c.fontSize,
        lineHeight: c.lineHeight,
        }}
    >
        {c.icon}
        {level}
    </span>
    );
};

const QuestionPreview = ({ text }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const textElement = textRef.current;
    if (!textElement) return undefined;

    const updateTruncation = () => {
      setIsTruncated(textElement.scrollWidth > textElement.clientWidth + 1);
    };

    updateTruncation();
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateTruncation)
      : null;

    resizeObserver?.observe(textElement);
    window.addEventListener('resize', updateTruncation);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateTruncation);
    };
  }, [text]);

  return (
    <div className={`qb-col-question qb-row-text qb-question-preview${isTruncated ? ' is-truncated' : ''}`}>
      <span ref={textRef} className="qb-question-preview-text">{text}</span>
      {isTruncated && (
        <div className="qb-question-preview-tooltip">
          {text}
        </div>
      )}
    </div>
  );
};

/* ── Sample Data ── */

/* ── Action Icon Buttons ── */
const EditIcon = () => (
  <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="35" height="35" rx="5.25" fill="#E5EBF9"/>
        <path d="M25.0059 13.5127L21.4867 9.99415C21.3697 9.87712 21.2308 9.78428 21.0779 9.72094C20.925 9.6576 20.7611 9.625 20.5956 9.625C20.4302 9.625 20.2663 9.6576 20.1134 9.72094C19.9605 9.78428 19.8216 9.87712 19.7046 9.99415L9.99433 19.7049C9.87682 19.8215 9.78366 19.9603 9.72026 20.1132C9.65686 20.2661 9.62448 20.4301 9.62501 20.5956V24.115C9.62501 24.4492 9.75775 24.7697 9.99404 25.0059C10.2303 25.2422 10.5508 25.375 10.885 25.375H24.1145C24.2815 25.375 24.4418 25.3086 24.5599 25.1905C24.6781 25.0723 24.7444 24.9121 24.7444 24.745C24.7444 24.5779 24.6781 24.4177 24.5599 24.2995C24.4418 24.1814 24.2815 24.115 24.1145 24.115H16.1862L25.0059 15.2949C25.1229 15.1779 25.2157 15.039 25.2791 14.8861C25.3424 14.7332 25.375 14.5693 25.375 14.4038C25.375 14.2383 25.3424 14.0744 25.2791 13.9216C25.2157 13.7687 25.1229 13.6298 25.0059 13.5127ZM14.4042 24.115H10.885V20.5956L17.8147 13.6655L21.3339 17.1849L14.4042 24.115ZM22.2245 16.2942L18.7061 12.7748L20.596 10.8848L24.1145 14.4042L22.2245 16.2942Z" fill="#6B7280"/>
    </svg>

);

const DeleteIcon = () => (
  <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="35" height="35" rx="5.25" fill="#E5EBF9"/>
        <path d="M24.3177 11.9215H10.6823C10.5179 11.9215 10.3603 11.9868 10.244 12.1031C10.1278 12.2193 10.0625 12.377 10.0625 12.5413C10.0625 12.7057 10.1278 12.8634 10.244 12.9796C10.3603 13.0958 10.5179 13.1611 10.6823 13.1611H11.3021V24.3174C11.3021 24.6461 11.4327 24.9614 11.6651 25.1939C11.8976 25.4264 12.2129 25.557 12.5417 25.557H22.4583C22.7871 25.557 23.1024 25.4264 23.3349 25.1939C23.5673 24.9614 23.6979 24.6461 23.6979 24.3174V13.1611H24.3177C24.4821 13.1611 24.6397 13.0958 24.756 12.9796C24.8722 12.8634 24.9375 12.7057 24.9375 12.5413C24.9375 12.377 24.8722 12.2193 24.756 12.1031C24.6397 11.9868 24.4821 11.9215 24.3177 11.9215ZM22.4583 24.3174H12.5417V13.1611H22.4583V24.3174ZM13.7812 10.0622C13.7812 9.8978 13.8465 9.74015 13.9628 9.62392C14.079 9.50768 14.2367 9.44238 14.401 9.44238H20.599C20.7633 9.44238 20.921 9.50768 21.0372 9.62392C21.1535 9.74015 21.2188 9.8978 21.2188 10.0622C21.2188 10.2266 21.1535 10.3842 21.0372 10.5004C20.921 10.6167 20.7633 10.682 20.599 10.682H14.401C14.2367 10.682 14.079 10.6167 13.9628 10.5004C13.8465 10.3842 13.7812 10.2266 13.7812 10.0622Z" fill="#6B7280"/>
    </svg>

);

/* ── Search Icon ── */
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.1008 12.2397L11.125 10.2639C11.7238 9.47999 12.0522 8.52916 12.0523 7.52583C12.0523 6.3167 11.5814 5.17987 10.7263 4.32491C9.87133 3.46994 8.73465 2.99902 7.52538 2.99902C6.31626 2.99902 5.17942 3.46994 4.32446 4.32491C2.55949 6.09002 2.55949 8.96193 4.32446 10.7267C5.17942 11.5819 6.31626 12.0528 7.52538 12.0528C8.52871 12.0526 9.47954 11.7242 10.2634 11.1254L12.2393 13.1013C12.3581 13.2202 12.5142 13.2797 12.6701 13.2797C12.8259 13.2797 12.982 13.2202 13.1008 13.1013C13.3388 12.8634 13.3388 12.4776 13.1008 12.2397ZM5.18597 9.86523C3.89608 8.57535 3.89623 6.47645 5.18597 5.18642C5.81083 4.5617 6.6417 4.21751 7.52538 4.21751C8.4092 4.21751 9.23992 4.5617 9.86478 5.18642C10.4896 5.81128 10.8338 6.64215 10.8338 7.52583C10.8338 8.40965 10.4896 9.24037 9.86478 9.86523C9.23992 10.4901 8.4092 10.8343 7.52538 10.8343C6.6417 10.8343 5.81083 10.4901 5.18597 9.86523Z" fill="#5B92E9"/>
  </svg>

);

/* ── Filter Icon ── */
const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M1.25 3.75C1.25 3.55109 1.32902 3.36032 1.46967 3.21967C1.61032 3.07902 1.80109 3 2 3H14C14.1989 3 14.3897 3.07902 14.5303 3.21967C14.671 3.36032 14.75 3.55109 14.75 3.75C14.75 3.94891 14.671 4.13968 14.5303 4.28033C14.3897 4.42098 14.1989 4.5 14 4.5H2C1.80109 4.5 1.61032 4.42098 1.46967 4.28033C1.32902 4.13968 1.25 3.94891 1.25 3.75ZM3.917 7.75C3.917 7.55109 3.99602 7.36032 4.13667 7.21967C4.27732 7.07902 4.46809 7 4.667 7H11.333C11.5319 7 11.7227 7.07902 11.8633 7.21967C12.004 7.36032 12.083 7.55109 12.083 7.75C12.083 7.94891 12.004 8.13968 11.8633 8.28033C11.7227 8.42098 11.5319 8.5 11.333 8.5H4.667C4.46809 8.5 4.27732 8.42098 4.13667 8.28033C3.99602 8.13968 3.917 7.94891 3.917 7.75ZM6.667 11C6.46809 11 6.27732 11.079 6.13667 11.2197C5.99602 11.3603 5.917 11.5511 5.917 11.75C5.917 11.9489 5.99602 12.1397 6.13667 12.2803C6.27732 12.421 6.46809 12.5 6.667 12.5H9.333C9.53191 12.5 9.72268 12.421 9.86333 12.2803C10.004 12.1397 10.083 11.9489 10.083 11.75C10.083 11.5511 10.004 11.3603 9.86333 11.2197C9.72268 11.079 9.53191 11 9.333 11H6.667Z" fill="#64748B"/>
  </svg>
);

/* ── ChevronDown Icon ── */
/* ── Table Section Icon ── */
const SectionHeaderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M8.25 1.91699C8.44891 1.91699 8.63968 1.99601 8.78033 2.13666C8.92098 2.27731 9 2.46808 9 2.66699V13.333C9 13.5319 8.92098 13.7227 8.78033 13.8633C8.63968 14.004 8.44891 14.083 8.25 14.083C8.05109 14.083 7.86032 14.004 7.71967 13.8633C7.57902 13.7227 7.5 13.5319 7.5 13.333V2.66699C7.5 2.46808 7.57902 2.27731 7.71967 2.13666C7.86032 1.99601 8.05109 1.91699 8.25 1.91699ZM12.25 5.91699C12.4489 5.91699 12.6397 5.99601 12.7803 6.13666C12.921 6.27731 13 6.46808 13 6.66699V13.333C13 13.5319 12.921 13.7227 12.7803 13.8633C12.6397 14.004 12.4489 14.083 12.25 14.083C12.0511 14.083 11.8603 14.004 11.7197 13.8633C11.579 13.7227 11.5 13.5319 11.5 13.333V6.66699C11.5 6.46808 11.579 6.27731 11.7197 6.13666C11.8603 5.99601 12.0511 5.91699 12.25 5.91699ZM5 9.33299C5 9.13408 4.92098 8.94331 4.78033 8.80266C4.63968 8.66201 4.44891 8.58299 4.25 8.58299C4.05109 8.58299 3.86032 8.66201 3.71967 8.80266C3.57902 8.94331 3.5 9.13408 3.5 9.33299V13.333C3.5 13.5319 3.57902 13.7227 3.71967 13.8633C3.86032 14.004 4.05109 14.083 4.25 14.083C4.44891 14.083 4.63968 14.004 4.78033 13.8633C4.92098 13.7227 5 13.5319 5 13.333V9.33299Z" fill="#5B92E9"/>
  </svg>
);

/* ── Table Difficulty Icon ── */
const DifficultyHeaderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.1647 6.05544C13.8924 6.01588 13.6325 5.91541 13.4044 5.76149C13.1763 5.60758 12.9859 5.40419 12.8473 5.16648C12.7087 4.92876 12.6255 4.66285 12.6039 4.38853C12.6039 4.11421 12.6229 3.83856 12.7227 3.5821C12.7861 3.41444 12.7954 3.23115 12.7494 3.05791C12.7033 2.88467 12.6043 2.73019 12.466 2.6161C11.8556 2.10355 11.1608 1.7009 10.4127 1.42611C10.2423 1.36282 10.0563 1.35491 9.88118 1.40351C9.70605 1.45211 9.55073 1.55474 9.43734 1.69677C9.26667 1.91501 9.04854 2.09152 8.7995 2.21291C8.55047 2.3343 8.27705 2.39739 8 2.39739C7.72296 2.39739 7.44954 2.3343 7.20051 2.21291C6.95147 2.09152 6.73334 1.91501 6.56267 1.69677C6.44928 1.55474 6.29396 1.45211 6.11883 1.40351C5.94371 1.35491 5.75771 1.36282 5.58734 1.42611C4.89652 1.6798 4.25062 2.04214 3.67401 2.49944C3.52866 2.6145 3.42397 2.77308 3.37527 2.95194C3.32656 3.13081 3.3364 3.32057 3.40334 3.49344C3.51103 3.75668 3.55552 4.04149 3.53325 4.32504C3.51098 4.60858 3.42256 4.88295 3.2751 5.12616C3.12764 5.36936 2.92523 5.57461 2.68411 5.72546C2.44299 5.8763 2.16988 5.96854 1.88668 5.99477C1.70281 6.01443 1.53012 6.0926 1.39402 6.21778C1.25792 6.34295 1.16561 6.50852 1.13068 6.6901C1.04378 7.12023 1.00001 7.55795 1.00001 7.99677C0.999371 8.36414 1.02903 8.73094 1.08868 9.09343C1.11836 9.28083 1.20896 9.45323 1.34645 9.58398C1.48395 9.71473 1.66069 9.79654 1.84934 9.81677C2.13877 9.84393 2.41747 9.94002 2.66214 10.097C2.9068 10.254 3.1103 10.4673 3.25561 10.7191C3.40092 10.9708 3.4838 11.2538 3.49732 11.5441C3.51085 11.8345 3.45462 12.1239 3.33334 12.3881C3.25397 12.5599 3.23429 12.7534 3.27741 12.9377C3.32053 13.122 3.42398 13.2866 3.57134 13.4054C4.17806 13.9087 4.86614 14.3048 5.60601 14.5768C5.70062 14.6095 5.79989 14.6269 5.90001 14.6281C6.03729 14.6278 6.17249 14.5946 6.29429 14.5313C6.41609 14.4679 6.52092 14.3763 6.60001 14.2641C6.76633 14.0218 6.98929 13.8237 7.24953 13.6871C7.50977 13.5504 7.79942 13.4794 8.09334 13.4801C8.37812 13.4805 8.65889 13.5473 8.91328 13.6753C9.16768 13.8033 9.38867 13.9889 9.55867 14.2174C9.67175 14.3694 9.83192 14.4798 10.0142 14.5313C10.1966 14.5827 10.3908 14.5725 10.5667 14.5021C11.2432 14.2298 11.873 13.8535 12.4333 13.3868C12.5741 13.2704 12.6743 13.1124 12.7196 12.9354C12.7649 12.7585 12.7529 12.5718 12.6853 12.4021C12.5756 12.1422 12.5277 11.8604 12.5455 11.5789C12.5634 11.2974 12.6464 11.0239 12.788 10.78C12.9297 10.536 13.1261 10.3284 13.3618 10.1733C13.5974 10.0183 13.8659 9.92023 14.146 9.88677C14.3276 9.86165 14.4965 9.77937 14.6283 9.65184C14.76 9.5243 14.8477 9.35815 14.8787 9.17743C14.9536 8.78819 14.9942 8.39312 15 7.99677C15.0001 7.5787 14.961 7.16155 14.8833 6.75077C14.8518 6.57404 14.7654 6.41174 14.6364 6.28691C14.5074 6.16209 14.3423 6.0811 14.1647 6.05544ZM13.9687 8.96743C13.5579 9.02743 13.166 9.17937 12.8222 9.41192C12.4784 9.64447 12.1915 9.95163 11.9829 10.3105C11.7743 10.6694 11.6494 11.0707 11.6175 11.4846C11.5856 11.8984 11.6475 12.3142 11.7987 12.7008C11.3359 13.0886 10.8166 13.4033 10.2587 13.6341C10.0019 13.3005 9.67178 13.0305 9.29383 12.8452C8.91587 12.6599 8.50029 12.5641 8.07934 12.5654C7.64623 12.5678 7.21969 12.6716 6.83393 12.8685C6.44818 13.0654 6.11393 13.35 5.85801 13.6994C5.25368 13.4696 4.68976 13.1451 4.18734 12.7381C4.35758 12.3467 4.434 11.9209 4.4105 11.4947C4.387 11.0685 4.26424 10.6538 4.05201 10.2834C3.83751 9.90904 3.53844 9.58999 3.17867 9.35177C2.81891 9.11355 2.40843 8.96276 1.98001 8.91143C1.94256 8.60806 1.92696 8.30238 1.93334 7.99677C1.93346 7.635 1.96784 7.27405 2.03601 6.91877C2.45389 6.86981 2.85499 6.72573 3.20851 6.4976C3.56203 6.26947 3.85856 5.96336 4.07534 5.60277C4.29182 5.24585 4.42372 4.84411 4.46093 4.42834C4.49814 4.01256 4.43967 3.59379 4.29001 3.2041C4.76624 2.83052 5.29653 2.53154 5.86267 2.31744C6.1218 2.63383 6.44794 2.88875 6.81756 3.0638C7.18717 3.23884 7.59104 3.32964 8 3.32964C8.40897 3.32964 8.81284 3.23884 9.18245 3.0638C9.55207 2.88875 9.87821 2.63383 10.1373 2.31744C10.7525 2.54987 11.3249 2.88259 11.8313 3.3021C11.693 3.68314 11.6401 4.08996 11.6766 4.4937C11.7131 4.89744 11.838 5.28821 12.0424 5.63828C12.2468 5.98834 12.5258 6.28913 12.8595 6.5193C13.1932 6.74947 13.5735 6.90337 13.9733 6.9701C14.034 7.30898 14.0653 7.65249 14.0667 7.99677C14.0654 8.32189 14.0389 8.64642 13.9873 8.96743H13.9687Z" fill="#5B92E9"/>
        <path d="M7.99995 5.19629C7.44616 5.19629 6.90481 5.36051 6.44435 5.66817C5.9839 5.97584 5.62501 6.41314 5.41309 6.92477C5.20116 7.43641 5.14572 7.99939 5.25375 8.54254C5.36179 9.08569 5.62847 9.5846 6.02005 9.97618C6.41164 10.3678 6.91055 10.6344 7.4537 10.7425C7.99684 10.8505 8.55983 10.7951 9.07146 10.5831C9.5831 10.3712 10.0204 10.0123 10.3281 9.55188C10.6357 9.09143 10.7999 8.55007 10.7999 7.99629C10.7999 7.25368 10.5049 6.54149 9.97985 6.01639C9.45475 5.49129 8.74256 5.19629 7.99995 5.19629ZM7.99995 9.86295C7.63076 9.86295 7.26986 9.75347 6.96289 9.54836C6.65592 9.34325 6.41666 9.05172 6.27538 8.71063C6.13409 8.36954 6.09713 7.99422 6.16915 7.63212C6.24118 7.27002 6.41896 6.93741 6.68002 6.67636C6.94108 6.4153 7.27368 6.23751 7.63578 6.16549C7.99788 6.09346 8.3732 6.13043 8.71429 6.27171C9.05538 6.413 9.34691 6.65225 9.55202 6.95922C9.75714 7.2662 9.86661 7.6271 9.86661 7.99629C9.86661 8.49136 9.66995 8.96615 9.31988 9.31622C8.96981 9.66629 8.49502 9.86295 7.99995 9.86295Z" fill="#5B92E9"/>
    </svg>

);

/* ── Pagination Chevrons ── */

/* ── Bulk Upload Dropzone ── */
function BulkUploadDropzone({ onClose, onSuccess, sectionId }) {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.name.endsWith('.csv')) setFile(dropped);
  };

  const handleClearFile = () => {
    setFile(null);
    setUploadResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file || !sectionId) return;
    setUploading(true);
    try {
      const token = getActiveToken();
      const formData = new FormData();
      formData.append('file', file);
      if (sectionId) formData.append('sectionId', sectionId);
      const res = await fetch('/api/questions/bulk', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUploadResult({ type: 'error', title: 'Import failed', message: data.message || 'Upload failed' });
        return;
      }

      const inserted = Number(data.inserted || 0);
      const failedCount = Array.isArray(data.errors) ? data.errors.length : 0;
      const nothingImported = inserted === 0;
      setUploadResult({
        type: nothingImported ? 'error' : data.errors?.length ? 'warning' : 'success',
        title: nothingImported
          ? 'Questions were not imported'
          : data.errors?.length
            ? `${inserted} question(s) imported`
            : `${inserted} question(s) imported successfully`,
        message: nothingImported
          ? (failedCount > 0 ? 'No questions were imported. Please review the skipped rows and try again.' : 'No questions were imported.')
          : data.errors?.length
            ? 'Some rows were skipped during import.'
            : 'Your question bank has been updated.',
        errors: data.errors || [],
      });
      onSuccess?.({ keepOpen: true, inserted, errors: data.errors || [] });
    } catch {
      setUploadResult({ type: 'error', title: 'Server error', message: 'Please try again in a moment.' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`qb-bulk-dropzone ${dragging ? 'qb-bulk-dropzone--active' : ''}`}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <div style={{ width: '48px', height: '48px', background: '#EFF6FF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 15V4M12 4L8 8M12 4L16 8" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4 15V19C4 19.552 4.448 20 5 20H19C19.552 20 20 19.552 20 19V15" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>

      {file ? (
        <div className="qb-bulk-file-selected">
        <p style={{ margin: 0, fontSize: '16px', color: '#101828', textAlign: 'center' }}>
          <strong>{file.name}</strong> — ready to upload
        </p>
        <button
          type="button"
          className="qb-bulk-clear-btn"
          onClick={handleClearFile}
          disabled={uploading}
        >
          Clear File
        </button>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '16px', color: '#101828' }}>Drag &amp; drop your file here</p>
          <p style={{ margin: '4px 0 0', fontSize: '16px', color: '#6A7282' }}>
            or <span style={{ color: '#155DFC', fontWeight: 500, cursor: 'pointer' }} onClick={() => inputRef.current?.click()}>browse files</span>
          </p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={e => {
          if (e.target.files[0]) {
            setFile(e.target.files[0]);
            setUploadResult(null);
          }
        }}
      />

      {uploadResult && (
        <div className={`qb-import-result qb-import-result--${uploadResult.type}`}>
          <div className="qb-import-result-icon">
            {uploadResult.type === 'success' ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 8V12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"/>
                <path d="M10.29 3.86L1.82 18A2 2 0 0 0 3.53 21H20.47A2 2 0 0 0 22.18 18L13.71 3.86A2 2 0 0 0 10.29 3.86Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <div className="qb-import-result-copy">
            <strong>{uploadResult.title}</strong>
            <span>{uploadResult.message}</span>
            {uploadResult.errors?.length > 0 && (
              <div className="qb-import-result-errors">
                {uploadResult.errors.slice(0, 4).map((error, index) => (
                  <div key={`${error}-${index}`}>{error}</div>
                ))}
                {uploadResult.errors.length > 4 && <div>+{uploadResult.errors.length - 4} more skipped rows</div>}
              </div>
            )}
          </div>
        </div>
      )}

      <button
        className="qb-se-submit"
        disabled={!file || !sectionId || uploading}
        onClick={handleUpload}
        style={{ marginTop: '8px', opacity: (!file || !sectionId) ? 0.5 : 1, cursor: (!file || !sectionId) ? 'not-allowed' : 'pointer' }}
      >
        {uploading ? 'Uploading...' : 'Import Questions'}
      </button>
    </div>
  );
}

/* ── Main Component ── */
export default function QuestionBank() {
  const [searchQuery, setSearchQuery]     = useState('');
  const [currentPage, setCurrentPage]     = useState(1);
  const [rowsPerPage, setRowsPerPage]     = useState(10);
  const [sectionFilter, setSectionFilter] = useState('All Sections');
  const [diffFilter, setDiffFilter]       = useState('All Difficulties');

  const handleSectionFilter = (v) => { setSectionFilter(v); setCurrentPage(1); };
  const handleDiffFilter    = (v) => { setDiffFilter(v);    setCurrentPage(1); };
  const handleSearch        = (v) => { setSearchQuery(v);   setCurrentPage(1); };
  const [showAddModal, setShowAddModal]       = useState(false);
  const [showSingleEntry, setShowSingleEntry] = useState(false);
  const [showBulkUpload, setShowBulkUpload]   = useState(false);
  const [isBulkSectionMenuOpen, setIsBulkSectionMenuOpen] = useState(false);
  const [isQuestionSectionMenuOpen, setIsQuestionSectionMenuOpen] = useState(false);
  const [isQuestionDifficultyMenuOpen, setIsQuestionDifficultyMenuOpen] = useState(false);
  const [bulkSectionId, setBulkSectionId]     = useState('');
  const [sections, setSections]               = useState([]);
  const [dbQuestions, setDbQuestions]         = useState([]);
  const [qForm, setQForm] = useState(EMPTY_QUESTION_FORM);
  const [editingId, setEditingId]             = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const bulkSectionDropdownRef = useRef(null);
  const questionSectionDropdownRef = useRef(null);
  const questionDifficultyDropdownRef = useRef(null);

  const isSuperAdminView = location.pathname.startsWith('/superadmin');
  const storedUser = getStoredUserForRole(isSuperAdminView ? 'superadmin' : 'recruiter');

  const selectedBulkSection = sections.find((section) => String(section.id) === String(bulkSectionId));
  const selectedQuestionSection = sections.find((section) => String(section.id) === String(qForm.section_id));
  const difficultyOptions = ['Easy', 'Medium', 'Hard'];

  useEffect(() => {
    if (!showSingleEntry) return;
    saveDraft(getQuestionDraftKey(editingId), qForm);
  }, [editingId, qForm, showSingleEntry]);

  useEffect(() => {
    if (!showBulkUpload) return;
    saveDraft(BULK_QUESTION_DRAFT_KEY, { sectionId: bulkSectionId });
  }, [bulkSectionId, showBulkUpload]);

  useEffect(() => {
    if (!showBulkUpload || !isBulkSectionMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (bulkSectionDropdownRef.current && !bulkSectionDropdownRef.current.contains(event.target)) {
        setIsBulkSectionMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsBulkSectionMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isBulkSectionMenuOpen, showBulkUpload]);

  useEffect(() => {
    if (!showSingleEntry) return undefined;

    const handlePointerDown = (event) => {
      if (questionSectionDropdownRef.current && !questionSectionDropdownRef.current.contains(event.target)) {
        setIsQuestionSectionMenuOpen(false);
      }

      if (questionDifficultyDropdownRef.current && !questionDifficultyDropdownRef.current.contains(event.target)) {
        setIsQuestionDifficultyMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsQuestionSectionMenuOpen(false);
        setIsQuestionDifficultyMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showSingleEntry]);

  const fetchQuestions = () => {
    const token = getActiveToken();
    fetch('/api/questions', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setDbQuestions(d.questions || []))
      .catch(() => {});
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value).replace(/"/g, '""');
    return /[",\n]/.test(stringValue) ? `"${stringValue}"` : stringValue;
  };

  const handleExportQuestions = () => {
    if (!dbQuestions.length) {
      alert('There are no questions to export yet.');
      return;
    }

    const headers = [
      'id',
      'section_id',
      'section_name',
      'difficulty',
      'marks',
      'negative_marks',
      'question_text',
      'option_a',
      'option_b',
      'option_c',
      'option_d',
      'correct_option',
      'usage_count',
      'created_at',
    ];

    const csvRows = [
      headers.join(','),
      ...dbQuestions.map((question) => (
        headers.map((header) => escapeCsvValue(question[header])).join(',')
      )),
    ];

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const exportDate = new Date().toISOString().slice(0, 10);

    link.href = downloadUrl;
    link.download = `question-bank-${exportDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  useEffect(() => {
    const token = getActiveToken();
    fetch('/api/sections', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setSections(d.sections || []))
      .catch(() => {});
    fetchQuestions();
  }, []);


  const handleAddQuestion = async () => {
    const { section_id, difficulty, marks, negative_marks, question_text, options, correct_option } = qForm;
    if (!question_text.trim()) return alert('Question text is required');
    if (options.some(o => !o.trim())) return alert('All 4 options are required');
    try {
      const isEditing = Boolean(editingId);
      const token = getActiveToken();
      const url = editingId ? `/api/questions/${editingId}` : '/api/questions';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ section_id: section_id || null, difficulty, marks: parseInt(marks) || 1, negative_marks: parseInt(negative_marks) || 0, question_text, option_a: options[0], option_b: options[1], option_c: options[2], option_d: options[3], correct_option }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return alert(data.message || 'Failed to add question');
      clearDraft(getQuestionDraftKey(editingId));
      setShowSingleEntry(false);
      setEditingId(null);
      setQForm(EMPTY_QUESTION_FORM);
      fetchQuestions();
      setToast({
        message: isEditing ? 'Question updated successfully.' : 'Question created successfully.',
        type: 'success',
      });
    } catch (error) {
      console.error('Add question error:', error);
      alert('Unable to reach the server. Please try again.');
    }
  };

  const handleEditQuestion = (q) => {
    setEditingId(q.id);
    const defaultForm = {
      section_id: q.section_id || '',
      difficulty: q.difficulty || 'Medium',
      marks: String(q.marks || '01'),
      negative_marks: String(q.negative_marks || '00'),
      question_text: q.question_text || '',
      options: [q.option_a, q.option_b, q.option_c, q.option_d],
      correct_option: ['a','b','c','d'].indexOf(q.correct_option),
    };
    setQForm(loadDraft(getQuestionDraftKey(q.id), defaultForm));
    setShowSingleEntry(true);
  };

  const handleOpenQuestionEntry = () => {
    setEditingId(null);
    setQForm(loadDraft(getQuestionDraftKey(null), EMPTY_QUESTION_FORM));
    setShowSingleEntry(true);
  };

  const handleCloseQuestionEntry = () => {
    setShowSingleEntry(false);
    setIsQuestionSectionMenuOpen(false);
    setIsQuestionDifficultyMenuOpen(false);
    setEditingId(null);
    setQForm(EMPTY_QUESTION_FORM);
  };

  const handleCloseQuestionEntryOverlay = () => {
    setToast({
      message: 'Draft saved. You can continue editing this question later.',
      type: 'success',
    });
    handleCloseQuestionEntry();
  };

  const handleOpenBulkUpload = () => {
    const draft = loadDraft(BULK_QUESTION_DRAFT_KEY, { sectionId: '' });
    setBulkSectionId(draft.sectionId || '');
    setIsBulkSectionMenuOpen(false);
    setShowBulkUpload(true);
  };

  const handleCloseBulkUpload = () => {
    setShowBulkUpload(false);
    setIsBulkSectionMenuOpen(false);
    setBulkSectionId('');
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
    const token = getActiveToken();
      const res = await fetch(`/api/questions/${questionToDelete.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDeleteError(data.message || 'Could not delete this question.');
        return;
      }
      setQuestionToDelete(null);
      fetchQuestions();
    } catch {
      setDeleteError('Server error. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  /* filter rows */
  const filtered = dbQuestions.filter(q => {
    const matchSection = sectionFilter === 'All Sections' || q.section_name === sectionFilter;
    const matchDiff    = diffFilter === 'All Difficulties' || q.difficulty === diffFilter;
    const matchSearch  = (q.question_text || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchSection && matchDiff && matchSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  if (!isSuperAdminView && storedUser.role === 'junior_recruiter') {
    return <Navigate to="/sections" replace />;
  }

  return (
    <div className={isSuperAdminView ? 'sa-page sa-resource-page' : 'nr-page'}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* ── Navbar ── */}
      <div className="nr-navbar-wrapper">
        <img src={logo} alt="Inteliment Logo" className="nr-navbar-logo-img" />
        <div className="nr-navbar-greeting">
          <h2 className="nr-navbar-greeting-title">{isSuperAdminView ? 'SuperAdmin Portal' : 'Question Bank'}</h2>
          <p className="nr-navbar-greeting-sub">{isSuperAdminView ? 'Super Admin' : `Found ${filtered.length} of ${dbQuestions.length} questions across ${sections.length} sections`}</p>
        </div>

        <div className="nr-navbar-actions">
            <div className="qb-nav-btn-group">
              <button className="qb-nav-btn-outline" onClick={handleExportQuestions}>
                 <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.7161 7.39962C11.5525 7.39962 11.3957 7.46459 11.28 7.58023C11.1644 7.69587 11.0994 7.85272 11.0994 8.01626V10.4828C11.0994 10.6463 11.0345 10.8032 10.9188 11.1094C10.8032 11.0345 10.6463 11.0994 10.4828 11.0994H1.84991C1.68636 11.0994 1.52952 11.0345 1.41388 10.9188C1.29824 10.8032 1.23327 10.6463 1.23327 10.4828V8.01626C1.23327 7.85272 1.1683 7.69587 1.05266 7.58023C0.93702 7.46459 0.780177 7.39962 0.616635 7.39962C0.453093 7.39962 0.29625 7.46459 0.180608 7.58023C0.0649667 7.69587 0 7.85272 0 8.01626V10.4828C0 10.9734 0.1949 11.444 0.541825 11.7909C0.888749 12.1378 1.35928 12.3327 1.84991 12.3327H10.4828C10.9734 12.3327 11.444 12.1378 11.7909 11.7909C12.1378 11.444 12.3327 10.9734 12.3327 10.4828V8.01626C12.3327 7.85272 12.2677 7.69587 12.1521 7.58023C12.0365 7.46459 11.8796 7.39962 11.7161 7.39962ZM6.60416 0.178968C6.54552 0.122829 6.47637 0.0788231 6.40067 0.0494747C6.32686 0.0168515 6.24705 7.05768e-09 6.16635 0C6.08565 -7.05404e-09 6.00584 0.0168515 5.93203 0.0494747C5.85634 0.0788231 5.78719 0.122829 5.72854 0.178967L3.262 2.64551C3.14589 2.76162 3.08065 2.91911 3.08065 3.08332C3.08065 3.24753 3.14589 3.40502 3.262 3.52113C3.37812 3.63724 3.5356 3.70248 3.69981 3.70248C3.86402 3.70248 4.02151 3.63724 4.13762 3.52113L5.54972 2.10287L5.54972 8.0164C5.54972 8.17994 5.61468 8.33679 5.73032 8.45243C5.84597 8.56807 6.00281 8.63304 6.16635 8.63304C6.32989 8.63304 6.48674 8.56807 6.60238 8.45243C6.71802 8.33679 6.78299 8.17994 6.78299 8.0164L6.78299 2.10287L8.19508 3.52113C8.25258 3.57862 8.32083 3.62423 8.39595 3.65535C8.47107 3.68646 8.55158 3.70248 8.63289 3.70248C8.7142 3.70248 8.79471 3.68646 8.86983 3.65535C8.94495 3.62423 9.01321 3.57862 9.0707 3.52113C9.1282 3.46364 9.1738 3.39538 9.20492 3.32026C9.23604 3.24514 9.25205 3.16463 9.25205 3.08332C9.25205 3.00201 9.23604 2.9215 9.20492 2.84638C9.1738 2.77126 9.1282 2.703 9.0707 2.64551L6.60416 0.178968Z" fill="#4683E5"/>
                </svg>

                Export
              </button>
            </div>
            <button className="qb-nav-btn-primary" onClick={() => setShowAddModal(true)}>
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 0.5V10.5" stroke="white" strokeWidth="1.49976" strokeLinecap="round" strokeLinejoin="round"/><path d="M0.5 5.5H10.5" stroke="white" strokeWidth="1.49976" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Add New Questions
            </button>
            <div className="nr-navbar-profile"><ProfileDropdown user={storedUser.name || storedUser.email} email={storedUser.email} /></div>
        </div>
      </div>

      {/* ── Sidebar ── */}
      <aside className={isSuperAdminView ? 'sa-sidebar' : 'nr-sidebar'}>
        {(isSuperAdminView ? adminSidebarItems : sidebarItems.filter(item => !(storedUser.role === 'junior_recruiter' && item.path === '/question-bank'))).map(({ label, Icon, path }, index) => {
          const targetPath = path;
          const isActive = location.pathname === targetPath.split('?')[0] && (!targetPath.includes('?') || location.search === '?' + targetPath.split('?')[1]);
          return (
            <div
              key={label}
              onClick={() => {
                if (targetPath) {
                  navigate(targetPath);
                }
              }}
              className={`${isSuperAdminView ? 'sa-sidebar-item' : 'nr-sidebar-item'} ${isActive ? 'active' : ''}`}
              style={isSuperAdminView ? { marginTop: index === 0 ? '60px' : '0' } : { top: `${140 + index * 64}px` }}
            >
              <span className={isSuperAdminView ? 'sa-sidebar-icon' : 'nr-sidebar-icon'}><Icon active={isActive} /></span>
              <span className={isSuperAdminView ? undefined : 'nr-sidebar-text'}>{label}</span>
            </div>
          );
        })}
      </aside>

      {/* ── Main Content ── */}
      <main className={isSuperAdminView ? 'sa-main sa-resource-main' : 'nr-main'}>
        {isSuperAdminView && <h2 className="sa-section-title">Question Bank</h2>}
        <div className={isSuperAdminView ? 'sa-table-card sa-resource-card' : 'sa-resource-content'}>

        {/* ── Filter Bar ── */}
        <div className="qb-filter-bar">
          {/* Search */}
          <div className="qb-filter-search-box">
            <div className="qb-filter-input-inner">
              <SearchIcon />
              <input
                type="text"
                className="qb-filter-search-input"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="qb-filter-label">
            <FilterIcon />
            <span>Filters</span>
          </div>

          <div className="qb-filter-input-box">
            <PrettySelect
              value={sectionFilter}
              onChange={handleSectionFilter}
              icon={<SectionHeaderIcon />}
              options={['All Sections', ...sections.map(s => s.name)]}
            />
          </div>

          <div className="qb-filter-input-box">
            <PrettySelect
              value={diffFilter}
              onChange={handleDiffFilter}
              icon={<DifficultyHeaderIcon />}
              options={['All Difficulties', 'Easy', 'Medium', 'Hard']}
            />
          </div>

        </div>

        {/* ── Table ── */}
        <div className="qb-table-wrapper">

          {/* Table Header */}
          <div className="qb-table-header">
            <div className="qb-col-question">Question</div>
            <div className="qb-col-section">
              <SectionHeaderIcon />
              <span>Section</span>
            </div>
            <div className="qb-col-difficulty">
              <DifficultyHeaderIcon />
              <span>Difficulty</span>
            </div>
            <div className="qb-col-actions">Actions</div>
          </div>

          <div className="qb-table-body">
            {/* Table Rows */}
            {paginated.map((q, idx) => {
              const isAlt = idx % 2 === 1;
              return (
                <div key={q.id} className={`qb-table-row ${isAlt ? 'qb-table-row--alt' : ''}`}>
                  <QuestionPreviewCell text={q.question_text} />
                  <div className="qb-col-section qb-row-section">{q.section_name || '-'}</div>
                  <div className="qb-col-difficulty">
                    <DifficultyBadge level={q.difficulty || 'Medium'} />
                  </div>
                  <div className="qb-col-actions">
                    <button className="qb-action-btn" title="Edit" onClick={() => handleEditQuestion(q)}>
                      <EditIcon />
                    </button>
                    {isSuperAdminView && (
                      <button className="qb-action-btn" title="Delete" onClick={() => { setDeleteError(''); setQuestionToDelete(q); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Pagination ── */}
        <div className="qb-pagination">
        {/* Left: nav + pages */}
        <div className="qb-pagination-pages">
            <button className="qb-page-nav" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
            <span>{"<<"} Prev</span>
            </button>
            <button className="qb-page-nav" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
            <span>{"<"} Prev</span>
            </button>

            {pages.map(p => (
            <button
                key={p}
                className={`qb-page-btn ${currentPage === p ? 'active' : ''}`}
                onClick={() => setCurrentPage(p)}
            >
                {p}
            </button>
            ))}

            <button className="qb-page-nav" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            <span>Next {">"}</span>
            </button>
            <button className="qb-page-nav" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
            <span>Next {">>"}</span>
            </button>
        </div>

        {/* Right: rows selector + label */}
        <div className="qb-pagination-info">
            <select
              className="qb-rows-btn"
              value={rowsPerPage}
              onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ cursor: 'pointer' }}
            >
              {[5, 10, 15, 20].map(n => <option key={n} value={n}>{String(n).padStart(2,'0')}</option>)}
            </select>
            <span className="qb-pagination-count">Items per page</span>
        </div>
        </div>
        </div>
      </main>

        {showAddModal && (
        <div className="qb-modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="qb-modal" onClick={e => e.stopPropagation()}>
            <button className="qb-modal-close" onClick={() => setShowAddModal(false)}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path d="M10 1L1 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
                <path d="M1 1L10 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
                </svg>
            </button>
            <h2 className="qb-modal-title">Add Questions</h2>
            <p className="qb-modal-sub">How would you like to <br />add questions?</p>
            <div className="qb-modal-btns">
                <button className="qb-modal-option-btn" onClick={() => { setShowAddModal(false); handleOpenQuestionEntry(); }}>
                <span className="qb-modal-option-icon">
                    <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 12.016C0 5.37974 5.37974 0 12.016 0C18.6522 0 24.032 5.37974 24.032 12.016C24.032 18.6522 18.6522 24.032 12.016 24.032C5.37974 24.032 0 18.6522 0 12.016Z" fill="#EEF2FF"/>
                        <path d="M13.5684 14.4473C13.5684 14.5585 13.5354 14.6673 13.4736 14.7598C13.4118 14.8523 13.3239 14.9244 13.2211 14.9669C13.1183 15.0095 13.0052 15.0207 12.8961 14.999C12.787 14.9773 12.6868 14.9237 12.6081 14.845C12.5294 14.7663 12.4759 14.6661 12.4542 14.557C12.4325 14.4479 12.4436 14.3348 12.4862 14.232C12.5288 14.1292 12.6009 14.0414 12.6934 13.9796C12.7859 13.9178 12.8946 13.8848 13.0059 13.8848C13.155 13.8848 13.2981 13.944 13.4036 14.0495C13.5091 14.155 13.5684 14.2981 13.5684 14.4473ZM13.0059 9.38477C11.9718 9.38477 11.1309 10.1418 11.1309 11.0723V11.2598C11.1309 11.3592 11.1704 11.4546 11.2407 11.5249C11.311 11.5953 11.4064 11.6348 11.5059 11.6348C11.6053 11.6348 11.7007 11.5953 11.771 11.5249C11.8414 11.4546 11.8809 11.3592 11.8809 11.2598V11.0723C11.8809 10.5566 12.3857 10.1348 13.0059 10.1348C13.626 10.1348 14.1309 10.5566 14.1309 11.0723C14.1309 11.5879 13.626 12.0098 13.0059 12.0098C12.9064 12.0098 12.811 12.0493 12.7407 12.1196C12.6704 12.1899 12.6309 12.2853 12.6309 12.3848V12.7598C12.6309 12.8592 12.6704 12.9546 12.7407 13.0249C12.811 13.0953 12.9064 13.1348 13.0059 13.1348C13.1053 13.1348 13.2007 13.0953 13.271 13.0249C13.3414 12.9546 13.3809 12.8592 13.3809 12.7598V12.726C14.2359 12.569 14.8809 11.887 14.8809 11.0723C14.8809 10.1418 14.0399 9.38477 13.0059 9.38477ZM17.8809 12.0098C17.8809 12.9739 17.5949 13.9165 17.0593 14.7182C16.5236 15.5199 15.7622 16.1447 14.8714 16.5137C13.9807 16.8827 13.0005 16.9792 12.0548 16.7911C11.1091 16.603 10.2405 16.1387 9.55872 15.4569C8.87694 14.7751 8.41264 13.9065 8.22453 12.9608C8.03643 12.0152 8.13297 11.035 8.50195 10.1442C8.87093 9.25339 9.49577 8.49202 10.2975 7.95635C11.0991 7.42068 12.0417 7.13477 13.0059 7.13477C14.2984 7.13613 15.5376 7.65018 16.4515 8.56413C17.3654 9.47807 17.8795 10.7173 17.8809 12.0098ZM17.1309 12.0098C17.1309 11.1939 16.8889 10.3964 16.4357 9.71804C15.9824 9.03969 15.3382 8.51097 14.5844 8.19876C13.8307 7.88655 13.0013 7.80486 12.2011 7.96403C11.4009 8.12319 10.6659 8.51606 10.089 9.09295C9.51215 9.66984 9.11929 10.4048 8.96012 11.205C8.80096 12.0052 8.88265 12.8346 9.19486 13.5883C9.50707 14.3421 10.0358 14.9863 10.7141 15.4396C11.3925 15.8928 12.19 16.1348 13.0059 16.1348C14.0995 16.1335 15.148 15.6985 15.9213 14.9252C16.6946 14.1519 17.1296 13.1034 17.1309 12.0098Z" fill="#4683E5"/>
                    </svg>
                </span>
                Single Entry
                </button>
                <button className="qb-modal-option-btn" onClick={() => { setShowAddModal(false); handleOpenBulkUpload(); }}>
                <span className="qb-modal-option-icon">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M10.5 7.5v2a1 1 0 01-1 1h-7a1 1 0 01-1-1v-2" stroke="#4683E5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8.5 4L6 1.5 3.5 4" stroke="#4683E5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 1.5v7" stroke="#4683E5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
                Bulk Upload
                </button>
            </div>
            </div>
        </div>
        )}

        {questionToDelete && (
        <div className="qb-confirm-overlay" onClick={() => !deleting && setQuestionToDelete(null)}>
            <div className="qb-confirm-dialog" onClick={e => e.stopPropagation()}>
            <button className="qb-confirm-close" type="button" onClick={() => !deleting && setQuestionToDelete(null)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12" stroke="#64748B" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M4 4L12 12" stroke="#64748B" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
            </button>
            <div className="qb-confirm-icon">
                <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M27.8238 6.18259H5.15258C4.87927 6.18259 4.61716 6.29116 4.4239 6.48442C4.23064 6.67767 4.12207 6.93979 4.12207 7.2131C4.12207 7.4864 4.23064 7.74852 4.4239 7.94178C4.61716 8.13504 4.87927 8.24361 5.15258 8.24361H6.18309V26.7928C6.18309 27.3394 6.40023 27.8636 6.78675 28.2501C7.17326 28.6367 7.69749 28.8538 8.24411 28.8538H24.7323C25.2789 28.8538 25.8031 28.6367 26.1896 28.2501C26.5761 27.8636 26.7933 27.3394 26.7933 26.7928V8.24361H27.8238C28.0971 8.24361 28.3592 8.13504 28.5525 7.94178C28.7457 7.74852 28.8543 7.4864 28.8543 7.2131C28.8543 6.93979 28.7457 6.67767 28.5525 6.48442C28.3592 6.29116 28.0971 6.18259 27.8238 6.18259ZM24.7323 26.7928H8.24411V8.24361H24.7323V26.7928ZM10.3051 3.09106C10.3051 2.81775 10.4137 2.55563 10.607 2.36238C10.8002 2.16912 11.0623 2.06055 11.3356 2.06055H21.6407C21.914 2.06055 22.1762 2.16912 22.3694 2.36238C22.5627 2.55563 22.6712 2.81775 22.6712 3.09106C22.6712 3.36437 22.5627 3.62648 22.3694 3.81974C22.1762 4.013 21.914 4.12157 21.6407 4.12157H11.3356C11.0623 4.12157 10.8002 4.013 10.607 3.81974C10.4137 3.62648 10.3051 3.36437 10.3051 3.09106Z" fill="#DC2626"/>
                </svg>
            </div>
            <div className="qb-confirm-copy">
                <h2>Delete this question?</h2>
                <p>This question will be deleted permanently. You will not be able to access or restore it later.</p>
                {deleteError && <div className="qb-confirm-error">{deleteError}</div>}
            </div>
            <div className="qb-confirm-actions">
                <button className="qb-confirm-cancel" disabled={deleting} onClick={() => setQuestionToDelete(null)}>
                Cancel
                </button>
                <button className="qb-confirm-delete" disabled={deleting} onClick={handleDeleteQuestion}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
            </div>
            </div>
        </div>
        )}

        {showSingleEntry && (
            <div className="qb-modal-overlay" onClick={handleCloseQuestionEntryOverlay}>
                <div className="qb-single-entry-modal" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="qb-se-header">
                    <div>
                    <h2 className="qb-se-title">{editingId ? 'Edit Question' : 'New Questions'}</h2>
                    <p className="qb-se-sub">Basic Info</p>
                    </div>
                    <button className="qb-modal-close" onClick={handleCloseQuestionEntry}>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <path d="M10 1L1 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
                        <path d="M1 1L10 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
                    </svg>
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="qb-se-body">

                    {/* Row 1: Section + Difficulty */}
                    <div className="qb-se-row">
                    <div className="qb-se-field">
                        <label className="qb-se-label">Section <span style={{color:'#FB2C36'}}>*</span></label>
                        <div className={`qb-se-select ${isQuestionSectionMenuOpen ? 'qb-se-select--open' : ''}`} ref={questionSectionDropdownRef}>
                          <button
                            type="button"
                            className="qb-se-select-trigger"
                            onClick={() => {
                              setIsQuestionDifficultyMenuOpen(false);
                              setIsQuestionSectionMenuOpen((prev) => !prev);
                            }}
                            aria-haspopup="listbox"
                            aria-expanded={isQuestionSectionMenuOpen}
                          >
                            <span className={`qb-se-select-value ${selectedQuestionSection ? 'qb-se-select-value--selected' : ''}`}>
                              {selectedQuestionSection ? selectedQuestionSection.name : 'Select Section'}
                            </span>
                            <svg
                              className="qb-se-select-chevron"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M5 7.5L10 12.5L15 7.5" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>

                          {isQuestionSectionMenuOpen && (
                            <div className="qb-se-select-menu qb-se-select-menu--down" role="listbox" aria-label="Section">
                              <button
                                type="button"
                                className={`qb-se-select-option ${!qForm.section_id ? 'qb-se-select-option--selected' : ''}`}
                                onClick={() => {
                                  setQForm((p) => ({ ...p, section_id: '' }));
                                  setIsQuestionSectionMenuOpen(false);
                                }}
                              >
                                <span>Select Section</span>
                                {!qForm.section_id && (
                                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.5 9L7.5 12L13.5 6" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </button>
                              {sections.map((section) => {
                                const isSelected = String(section.id) === String(qForm.section_id);
                                return (
                                  <button
                                    key={section.id}
                                    type="button"
                                    className={`qb-se-select-option ${isSelected ? 'qb-se-select-option--selected' : ''}`}
                                    onClick={() => {
                                      setQForm((p) => ({ ...p, section_id: String(section.id) }));
                                      setIsQuestionSectionMenuOpen(false);
                                    }}
                                  >
                                    <span>{section.name}</span>
                                    {isSelected && (
                                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.5 9L7.5 12L13.5 6" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                    </div>
                    <div className="qb-se-field">
                        <label className="qb-se-label">Difficulty<span className="required-star">*</span></label>
                        <div className={`qb-se-select ${isQuestionDifficultyMenuOpen ? 'qb-se-select--open' : ''}`} ref={questionDifficultyDropdownRef}>
                          <button
                            type="button"
                            className="qb-se-select-trigger"
                            onClick={() => {
                              setIsQuestionSectionMenuOpen(false);
                              setIsQuestionDifficultyMenuOpen((prev) => !prev);
                            }}
                            aria-haspopup="listbox"
                            aria-expanded={isQuestionDifficultyMenuOpen}
                          >
                            <span className="qb-se-select-value qb-se-select-value--selected">
                              {qForm.difficulty || 'Medium'}
                            </span>
                            <svg
                              className="qb-se-select-chevron"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M5 7.5L10 12.5L15 7.5" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>

                          {isQuestionDifficultyMenuOpen && (
                            <div className="qb-se-select-menu qb-se-select-menu--down" role="listbox" aria-label="Difficulty">
                              {difficultyOptions.map((difficulty) => {
                                const isSelected = difficulty === qForm.difficulty;
                                return (
                                  <button
                                    key={difficulty}
                                    type="button"
                                    className={`qb-se-select-option ${isSelected ? 'qb-se-select-option--selected' : ''}`}
                                    onClick={() => {
                                      setQForm((p) => ({ ...p, difficulty }));
                                      setIsQuestionDifficultyMenuOpen(false);
                                    }}
                                  >
                                    <span>{difficulty}</span>
                                    {isSelected && (
                                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.5 9L7.5 12L13.5 6" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                    </div>
                    </div>

                    {/* Row 2: Marks + Negative Marks */}
                    <div className="qb-se-row">
                    <div className="qb-se-field">
                        <label className="qb-se-label">Marks<span className="required-star">*</span></label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="qb-se-input"
                          value={qForm.marks}
                          onWheel={preventValueScroll}
                          onChange={e => setQForm(p => ({ ...p, marks: keepDigitsOnly(e.target.value) }))}
                        />
                    </div>
                    <div className="qb-se-field">
                        <label className="qb-se-label">Negative Marks (Coming Soon)</label>
                        <input
                          type="text"
                          className="qb-se-input qb-se-input--disabled"
                          value="Coming Soon"
                          disabled
                          readOnly
                        />
                    </div>
                    </div>

                    {/* Question Text */}
                    <div className="qb-se-field qb-se-field--full">
                    <label className="qb-se-label">Question Text <span style={{color:'#FB2C36'}}>*</span></label>
                    <textarea className="qb-se-textarea" placeholder="Enter your question here..." value={qForm.question_text} onChange={e => setQForm(p => ({...p, question_text: e.target.value}))} />
                    </div>

                    {/* Options */}
                    <div className="qb-se-field qb-se-field--full">
                      <label className="qb-se-label">Options<span className="required-star">*</span></label>
                      {['A', 'B', 'C', 'D'].map((opt, i) => (
                        <div
                          key={opt}
                          className={`qb-se-option ${qForm.correct_option === i ? 'qb-se-option--selected' : ''}`}
                          onClick={() => setQForm(p => ({...p, correct_option: i}))}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="qb-se-radio">
                            {qForm.correct_option === i && <div className="qb-se-radio-dot" />}
                          </div>
                          <input
                            type="text"
                            className="qb-se-option-input"
                            placeholder={`Option ${opt}`}
                            value={qForm.options[i]}
                            onChange={e => { const opts = [...qForm.options]; opts[i] = e.target.value; setQForm(p => ({...p, options: opts})); }}
                            onClick={e => e.stopPropagation()}
                          />
                        </div>
                      ))}
                      <p className="qb-se-hint">Select the correct/best option and fill the rest</p>
                    </div>
                </div>

                {/* Footer */}
                <div className="qb-se-footer">
                    <button className="qb-se-submit" onClick={handleAddQuestion}>{editingId ? 'Update Question' : 'Add Question'}</button>
                </div>

                </div>
            </div>
        )}
      {showBulkUpload && (
        <div className="qb-modal-overlay" onClick={handleCloseBulkUpload}>
          <div className="qb-single-entry-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="qb-se-header">
              <div>
                <h2 className="qb-se-title">Bulk Import Questions</h2>
                <p className="qb-se-sub">Upload a CSV file to import multiple questions at once.</p>
              </div>
              <button className="qb-modal-close" onClick={handleCloseBulkUpload}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path d="M10 1L1 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
                  <path d="M1 1L10 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="qb-se-body">

              {/* CSV Template section */}
              <div className="qb-se-field qb-se-field--full">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="qb-se-label">CSV Template</label>
                  <a
                    href="/question-template.csv"
                    download="question-template.csv"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#155DFC', fontSize: '15px', fontWeight: 500, textDecoration: 'none' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 10.5V2M8 10.5L5 7.5M8 10.5L11 7.5" stroke="#155DFC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 11.5V13C3 13.552 3.448 14 4 14H12C12.552 14 13 13.552 13 13V11.5" stroke="#155DFC" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Download Template
                  </a>
                </div>
                <p style={{ margin: '0 0 8px', fontSize: '15px', color: '#4A5565', fontWeight: 500 }}>
                  Download and fill in the template
                </p>
                <div style={{ background: '#F0F4F7', borderRadius: '10px', padding: '16px', fontSize: '14px', color: 'rgba(38,40,43,0.5)', lineHeight: '24px' }}>
                  <div><span style={{ fontWeight: 500 }}>Required Columns :</span></div>
                  <div>text, option_a, option_b, option_c, option_d, correct_answer (A/B/C/D), difficulty (easy/medium/hard), marks</div>
                </div>
              </div>

              {/* Target Section dropdown */}
              <div className="qb-se-field qb-se-field--full">
                <label className="qb-se-label">Target Section <span style={{ color: '#FB2C36' }}>*</span></label>
                <div className={`qb-se-select ${isBulkSectionMenuOpen ? 'qb-se-select--open' : ''}`} ref={bulkSectionDropdownRef}>
                  <button
                    type="button"
                    className="qb-se-select-trigger"
                    onClick={() => setIsBulkSectionMenuOpen((prev) => !prev)}
                    aria-haspopup="listbox"
                    aria-expanded={isBulkSectionMenuOpen}
                  >
                    <span className={`qb-se-select-value ${selectedBulkSection ? 'qb-se-select-value--selected' : ''}`}>
                      {selectedBulkSection ? selectedBulkSection.name : 'Select Section'}
                    </span>
                    <svg
                      className="qb-se-select-chevron"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M5 7.5L10 12.5L15 7.5" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {isBulkSectionMenuOpen && (
                    <div className="qb-se-select-menu qb-se-select-menu--down" role="listbox" aria-label="Target Section">
                      <button
                        type="button"
                        className={`qb-se-select-option ${!bulkSectionId ? 'qb-se-select-option--selected' : ''}`}
                        onClick={() => {
                          setBulkSectionId('');
                          setIsBulkSectionMenuOpen(false);
                        }}
                      >
                        <span>Select Section</span>
                        {!bulkSectionId && (
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.5 9L7.5 12L13.5 6" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </button>
                      {sections.map((section) => {
                        const isSelected = String(section.id) === String(bulkSectionId);
                        return (
                          <button
                            key={section.id}
                            type="button"
                            className={`qb-se-select-option ${isSelected ? 'qb-se-select-option--selected' : ''}`}
                            onClick={() => {
                              setBulkSectionId(String(section.id));
                              setIsBulkSectionMenuOpen(false);
                            }}
                          >
                            <span>{section.name}</span>
                            {isSelected && (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4.5 9L7.5 12L13.5 6" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* CSV File drop zone */}
              <div className="qb-se-field qb-se-field--full">
                <label className="qb-se-label">CSV File <span style={{ color: '#FB2C36' }}>*</span></label>
                <BulkUploadDropzone
                  sectionId={bulkSectionId}
                  onClose={handleCloseBulkUpload}
                  onSuccess={({ inserted, errors }) => {
                    clearDraft(BULK_QUESTION_DRAFT_KEY);
                    setBulkSectionId('');
                    fetchQuestions();
                    setToast({
                      message: inserted > 0
                        ? (errors?.length ? `${inserted} question(s) imported. Some rows were skipped.` : 'Questions imported successfully.')
                        : 'Questions were not imported.',
                      type: inserted > 0 ? (errors?.length ? 'warning' : 'success') : 'error',
                    });
                  }}
                />
              </div>

            </div>

            {/* Footer */}
            <div className="qb-se-footer" style={{ gap: '12px' }}>
              <button
                className="qb-se-submit"
                style={{ background: '#fff', color: '#4D8BF1', border: '1px solid #4D8BF1' }}
                onClick={handleCloseBulkUpload}
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
